import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setContent,
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  useCreatePatientNoteMutation,
  useDeletePatientNoteMutation,
  useGetPatientNotesQuery,
  useUpdatePatientNoteMutation
} from "services/patient-notes";
import { useGetPatientQuery } from "services/patients";
import { PatientNote } from "services/types/patient-notes.type";
import type { Patient } from "services/types/patients.type";
import { safeRefetch } from "utils/services.util";

import { PATIENT_HEADER_QUERY } from "../use-patient.hook";
import { formatPatientNoteToInput } from "./components/patient-note.util";
import { PatientNotesFormValues } from "./components/patient-notes-form.component";

export function usePatientNotesDrawerProvider() {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [editMode, setEditMode] = useState<PatientNote>();
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [patientId, setPatientId] = useState<number>(-1);

  const [updatePatientNote, { isLoading: isUpdating }] =
    useUpdatePatientNoteMutation();

  const [createPatientNote, { isLoading: isCreating }] =
    useCreatePatientNoteMutation();

  const [deletePatientNote] = useDeletePatientNoteMutation();

  const {
    data: patient,
    refetch: refetchPatient,
    isUninitialized: isPatientUninitialized
  } = useGetPatientQuery(
    { patientId, query: PATIENT_HEADER_QUERY },
    {
      skip: patientId === -1
    }
  );

  const {
    data: notes,
    isFetching,
    refetch,
    isUninitialized: isNotesUninitialized
  } = useGetPatientNotesQuery(
    {
      patientId,
      is_active: true
    },
    {
      skip: patientId === -1 || !isOpen,
    }
  );

  const refetchAll = useCallback(() => {
    safeRefetch(refetchPatient, isPatientUninitialized);
    safeRefetch(refetch, isNotesUninitialized);
  }, [refetchPatient, isPatientUninitialized, refetch, isNotesUninitialized]);

  const handleDelete = useCallback(
    (note: PatientNote) => {
      dispatch(setTitle("Are you sure you want to delete the note?"));
      dispatch(
        setOnChange(() => {
          deletePatientNote({
            patientId: patientId,
            id: note.id
          }).then(() => {
            refetchAll();
          });
        })
      );
      dispatch(setOpen(true));
      dispatch(setContent(`Delete "${note.title}"?`));
    },
    [deletePatientNote, refetchAll, dispatch, patientId]
  );

  const handleClose = useCallback(() => {
    setIsOpenCreate(false);
    setIsOpen(false);
    setEditMode(undefined);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setIsOpenCreate(false);
    setEditMode(undefined);
  }, []);

  const handleCreate = useCallback(
    (data: PatientNotesFormValues) => {
      createPatientNote(formatPatientNoteToInput(data)).then((response) => {
        refetchAll();

        handleCloseCreate();
      });
    },
    [createPatientNote, handleCloseCreate, refetchAll]
  );

  const handleUpdate = useCallback(
    (data: PatientNotesFormValues) => {
      updatePatientNote(formatPatientNoteToInput(data)).then((response) => {
        refetchAll();
        handleCloseCreate();
      });
    },
    [updatePatientNote, refetchAll, handleCloseCreate]
  );

  const handleOpen = useCallback((patientId?: number) => {
    if ((typeof patientId === "number" || patientId !== -1) && patientId) {
      setPatientId(Number(patientId));
    }
    setIsOpen(true);
  }, []);
  const handleOpenCreate = useCallback((patientId?: number) => {
    if ((typeof patientId === "number" || patientId !== -1) && patientId) {
      setPatientId(Number(patientId));
    }
    setIsOpenCreate(true);
    setIsOpen(true);
    setEditMode(undefined);
  }, []);

  const handleEdit = useCallback((note: PatientNote) => {
    setIsOpenCreate(true);
    setIsOpen(true);
    setEditMode(note);
  }, []);

  return {
    isOpen,
    isOpenCreate,
    notes,
    isFetching,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    handleEdit,
    editMode,
    isCreating,
    isUpdating,
    handleCreate,
    handleUpdate,
    patient: patientId === -1 ? undefined : patient
  };
}

export const PatientNotesDrawerContext = createContext<{
  isOpen: boolean;
  isOpenCreate: boolean;
  isFetching: boolean;
  notes?: PatientNote[];
  handleOpen: (patientId?: number) => void;
  handleOpenCreate: (patientId?: number) => void;
  handleClose: () => void;
  handleCloseCreate: () => void;
  handleDelete: (note: PatientNote) => void;
  handleEdit: (note: PatientNote) => void;
  handleCreate: (data: PatientNotesFormValues) => void;
  handleUpdate: (data: PatientNotesFormValues) => void;
  editMode?: PatientNote;
  setPatientId: React.Dispatch<React.SetStateAction<number>>;
  isCreating: boolean;
  isUpdating: boolean;
  patient?: Patient;
} | null>(null);

type PatientDrawerProviderProps = {
  children?: ReactNode;
};

export const PatientNotesDrawerProvider = ({
  children
}: PatientDrawerProviderProps) => {
  const {
    isOpen,
    isOpenCreate,
    isFetching,
    notes,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    handleEdit,
    editMode,
    isCreating,
    isUpdating,
    handleCreate,
    handleUpdate,
    patient
  } = usePatientNotesDrawerProvider();

  const value = useMemo(
    () => ({
      isOpen,
      isOpenCreate,
      isFetching,
      notes,
      handleOpen,
      handleOpenCreate,
      handleClose,
      handleCloseCreate,
      handleDelete,
      setPatientId,
      editMode,
      handleEdit,
      isCreating,
      isUpdating,
      handleCreate,
      handleUpdate,
      patient
    }),
    [
      isOpen,
      isOpenCreate,
      isFetching,
      notes,
      handleOpen,
      handleOpenCreate,
      handleClose,
      handleCloseCreate,
      handleDelete,
      setPatientId,
      editMode,
      handleEdit,
      isCreating,
      isUpdating,
      handleCreate,
      handleUpdate,
      patient
    ]
  );

  return (
    <PatientNotesDrawerContext.Provider value={value}>
      {children}
    </PatientNotesDrawerContext.Provider>
  );
};

export const usePatientNotesDrawer = () => {
  const context = useContext(PatientNotesDrawerContext);
  if (!context) {
    throw new Error(
      `usePatientNotesDrawer must be used within a PatientNotesDrawerContext`
    );
  }

  const {
    isOpen,
    isOpenCreate,
    isFetching,
    notes,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    editMode,
    handleEdit,
    isCreating,
    isUpdating,
    handleCreate,
    handleUpdate,
    patient
  } = context;

  return {
    isOpen,
    isOpenCreate,
    isFetching,
    notes,
    handleOpen,
    handleOpenCreate,
    handleClose,
    handleCloseCreate,
    handleDelete,
    setPatientId,
    editMode,
    handleEdit,
    isCreating,
    isUpdating,
    handleCreate,
    handleUpdate,
    patient
  };
};
